import "core-js/modules/es6.array.is-array";
import "core-js/modules/es6.regexp.replace";
import React from 'react';
import deepEqual from 'dequal';

function isPrimitive(val) {
  return val == null || /^[sbn]/.test(typeof val);
}

function checkDeps(deps, name) {
  var reactHookName = "React.".concat(name.replace(/DeepCompare/, ''));

  if (!deps || deps.length === 0) {
    throw new Error("".concat(name, " should not be used with no dependencies. Use ").concat(reactHookName, " instead."));
  }

  if (deps.every(isPrimitive)) {
    throw new Error("".concat(name, " should not be used with dependencies that are all primitive values. Use ").concat(reactHookName, " instead."));
  }
}

function useDeepCompareMemoize(value) {
  var ref = React.useRef([]);

  if (!deepEqual(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}
/**
 * `useDeepCompareEffect` will return a memoized version of the callback that
 * only changes if one of the `deps` has changed.
 *
 * Usage note: only use this if `deps` are objects or arrays that contain
 * objects. Otherwise you should just use React.useEffect.
 *
 */


function useDeepCompareCallback(callback, dependencies) {
  if (process.env.NODE_ENV !== 'production') {
    checkDeps(dependencies, 'useDeepCompareCallback');
  }

  return React.useCallback(callback, useDeepCompareMemoize(dependencies));
}
/**
 * `useDeepCompareEffect` Accepts a function that contains imperative, possibly
 * effectful code.
 *
 * @param effect Imperative function that can return a cleanup function
 * @param deps If present, effect will only activate if the values in the list
 * change.
 *
 * Usage note: only use this if `deps` are objects or arrays that contain
 * objects. Otherwise you should just use React.useEffect.
 *
 */


function useDeepCompareEffect(effect, dependencies) {
  if (process.env.NODE_ENV !== 'production') {
    checkDeps(dependencies, 'useDeepCompareEffect');
  }

  React.useEffect(effect, useDeepCompareMemoize(dependencies));
}
/**
 * `useDeepCompareMemo` will only recompute the memoized value when one of the
 * `deps` has changed.
 *
 * Usage note: only use this if `deps` are objects or arrays that contain
 * objects. Otherwise you should just use React.useMemo.
 *
 */


function useDeepCompareMemo(factory, dependencies) {
  if (process.env.NODE_ENV !== 'production') {
    checkDeps(dependencies, 'useDeepCompareMemo');
  }

  return React.useMemo(factory, useDeepCompareMemoize(dependencies));
}

export { useDeepCompareCallback, useDeepCompareEffect, useDeepCompareMemo };